import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, User, Mail, Lock, ArrowRight, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_expert-connect-184/artifacts/zgfr8qi0_Consultant.svg";

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [userType, setUserType] = useState('professional');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [googleOAuthEnabled, setGoogleOAuthEnabled] = useState(false);
  
  // Anti-robot challenge
  const [challenge, setChallenge] = useState({ num1: 0, num2: 0, answer: 0 });
  const [userAnswer, setUserAnswer] = useState('');
  const [challengeError, setChallengeError] = useState('');

  // Generate a new math challenge
  const generateChallenge = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setChallenge({ num1, num2, answer: num1 + num2 });
    setUserAnswer('');
    setChallengeError('');
  };

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam) {
      setUserType(typeParam);
      setIsLogin(false);
    }
    
    // Google OAuth is always enabled with Emergent Auth
    setGoogleOAuthEnabled(true);
    
    // Generate initial challenge
    generateChallenge();
  }, [searchParams]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    localStorage.setItem('pending_user_type', userType);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Verify challenge for registration
    if (!isLogin) {
      if (parseInt(userAnswer) !== challenge.answer) {
        setChallengeError('Risposta errata. Riprova.');
        generateChallenge();
        return;
      }
    }
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { 
            ...formData, 
            user_type: userType,
            captcha_num1: challenge.num1,
            captcha_num2: challenge.num2,
            captcha_answer: parseInt(userAnswer)
          };

      const response = await axios.post(`${API}${endpoint}`, payload);
      const { token, user } = response.data;
      
      login(token, user);
      toast.success(isLogin ? 'Accesso effettuato!' : 'Registrazione completata!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Si è verificato un errore');
      // Regenerate challenge on error
      if (!isLogin) {
        generateChallenge();
      }
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    'Accesso a 500+ professionisti qualificati',
    'Messaggistica integrata e sicura',
    'Nessun costo nascosto',
    'Supporto dedicato'
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <div 
              className="flex items-center mb-8 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/')}
              data-testid="auth-logo"
            >
              <img src={LOGO_URL} alt="ConsultantHUB" className="h-16 w-auto" />
            </div>
            <h1 className="text-3xl font-playfair font-bold text-[#344B71] mb-2">
              {isLogin ? 'Bentornato!' : 'Crea il tuo Account'}
            </h1>
            <p className="text-slate-600">
              {isLogin ? 'Accedi per continuare su ConsultantHUB' : 'Inizia gratuitamente in pochi secondi'}
            </p>
          </div>

          {/* Google OAuth Button */}
          <Button
            type="button"
            onClick={handleGoogleLogin}
            disabled={!googleOAuthEnabled}
            className="w-full mb-6 bg-white hover:bg-slate-50 text-slate-700 border-2 border-slate-200 rounded-xl h-12 font-medium transition-all shadow-sm hover:shadow-md hover:border-slate-300 flex items-center justify-center space-x-3"
            data-testid="google-login-button"
          >
            <GoogleIcon />
            <span>{isLogin ? 'Continua con Google' : 'Registrati con Google'}</span>
          </Button>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-slate-200"></div>
            <span className="px-4 text-sm text-slate-400">oppure</span>
            <div className="flex-1 border-t border-slate-200"></div>
          </div>

          {!isLogin && (
            <div className="mb-6" data-testid="user-type-selector">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">
                Tipo di Account
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setUserType('professional')}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    userType === 'professional'
                      ? 'border-[#344B71] bg-[#344B71]/5 text-[#344B71]'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-[#344B71]/50'
                  }`}
                  data-testid="professional-type-button"
                >
                  <User className={`w-6 h-6 mx-auto mb-2 ${userType === 'professional' ? 'text-[#344B71]' : ''}`} />
                  <span className="text-sm font-medium">Professionista</span>
                </button>
                <button
                  type="button"
                  onClick={() => setUserType('company')}
                  className={`p-4 border-2 rounded-xl transition-all ${
                    userType === 'company'
                      ? 'border-[#344B71] bg-[#344B71]/5 text-[#344B71]'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-[#344B71]/50'
                  }`}
                  data-testid="company-type-button"
                >
                  <Building2 className={`w-6 h-6 mx-auto mb-2 ${userType === 'company' ? 'text-[#344B71]' : ''}`} />
                  <span className="text-sm font-medium">Azienda</span>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-slate-700 font-medium">Nome Completo</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Mario Rossi"
                  className="mt-1 h-12 rounded-xl border-slate-200 focus:border-[#344B71] focus:ring-[#344B71]"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="mario@esempio.it"
                className="mt-1 h-12 rounded-xl border-slate-200 focus:border-[#344B71] focus:ring-[#344B71]"
                required
                data-testid="email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="mt-1 h-12 rounded-xl border-slate-200 focus:border-[#344B71] focus:ring-[#344B71]"
                required
                data-testid="password-input"
              />
            </div>

            {/* Anti-robot Challenge - Only for registration */}
            {!isLogin && (
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Verifica di sicurezza
                </Label>
                <p className="text-slate-600 mb-3">
                  Quanto fa <span className="font-bold text-[#344B71]">{challenge.num1} + {challenge.num2}</span>?
                </p>
                <Input
                  type="number"
                  value={userAnswer}
                  onChange={(e) => {
                    setUserAnswer(e.target.value);
                    setChallengeError('');
                  }}
                  placeholder="Inserisci la risposta"
                  className="h-12 rounded-xl border-slate-200 focus:border-[#344B71] focus:ring-[#344B71]"
                  required
                  data-testid="challenge-input"
                />
                {challengeError && (
                  <p className="text-red-500 text-sm mt-2">{challengeError}</p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl h-12 font-semibold transition-all shadow-lg hover:shadow-xl mt-6"
              data-testid="submit-button"
            >
              {loading ? 'Caricamento...' : (isLogin ? 'Accedi' : 'Crea Account')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#344B71] hover:text-[#344B71] text-sm font-medium"
              data-testid="toggle-auth-button"
            >
              {isLogin ? 'Non hai un account? Registrati gratis' : 'Hai già un account? Accedi'}
            </button>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Benefits */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-[#344B71] via-[#344B71] to-[#344B71] items-center justify-center p-12 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <motion.div 
          className="relative z-10 max-w-lg text-white"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h2 className="text-3xl font-playfair font-bold mb-6">
            Il modo più semplice per trovare consulenti professionali
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Unisciti a migliaia di professionisti e aziende che usano ConsultantHUB per crescere insieme.
          </p>
          
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={index}
                className="flex items-center space-x-3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
              >
                <div className="w-6 h-6 bg-[#E8A007] rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="text-white/90">{benefit}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-white/60 text-sm">
              &ldquo;ConsultantHUB ci ha permesso di trovare il professionista giusto in meno di 24 ore.&rdquo;
            </p>
            <p className="text-white font-medium mt-2">— Andrea Basile, CEO Evolution S.p.A.</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
